import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { Contract, ContractOrderType } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { getContractTradeType } from "@/utils/Contracts";
import { useGetContractOrders } from "@/services/Contracts";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import Alert from "@/components/atoms/Alert";
import CTA from "@/components/atoms/CTA";
import Group from "@/components/atoms/Group";
import Heading from "@/components/atoms/Heading";
import Spinner from "@/components/atoms/Spinner/Spinner";
import Stack from "@/components/atoms/Stack";
import ContractsTable from "./ContractsTable";
import styles from "../styles.module.css";

const DEFAULT_ORDERS_PER_PAGE = 15;

interface Props {
  contract: Contract;
}

/**
 * The orders on a contract, fetched page by page from their own endpoint so that opening a contract does not
 * have to load all of them. Loading and error states are independent of the contract query.
 */
const ContractOrders: FC<Props> = ({ contract }: Props) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ORDERS_PER_PAGE);

  // The table shows the side of the contract the current company is on
  const orderType: ContractOrderType =
    getContractTradeType(contract, currentCompany) === ContractTradeTypeEnum.SELL ? "SELL" : "BUY";

  const {
    data: orders,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useGetContractOrders(contract.id, { page: activePage, pageSize: rowsPerPage, orderType });

  useEffect(() => {
    if (isError) {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.fetchError"), t("notifications:contracts.ordersFetchError"))
      );
    }
  }, [isError]);

  // The last page can disappear when the total shrinks, so never stay on a page past the end
  const lastPage = Math.max(1, Math.ceil((orders?.totalCount ?? 0) / rowsPerPage));
  useEffect(() => {
    if (activePage > lastPage) setActivePage(lastPage);
  }, [activePage, lastPage]);

  const handleRowsPerPageChange = (value: number) => {
    if (!value) return;
    setRowsPerPage(value);
    setActivePage(1);
  };

  return (
    <div className={styles.contractsTable}>
      <header className={styles.contractsTableHeader}>
        <Group>
          <Heading order={5} strong={true} data-testid="contract-orders-count">
            {orders?.totalCount ?? "-"} {t("contracts:ordersOnContract")}
          </Heading>
          <Spinner inline loading={isFetching} />
        </Group>
      </header>
      {isError ? (
        <Stack p="md" align="flex-start">
          <Alert color="error">{t("notifications:contracts.ordersFetchError")}</Alert>
          <CTA size="sm" intent="secondary" onClick={() => refetch()}>
            {t("actions:retry")}
          </CTA>
        </Stack>
      ) : (
        <ContractsTable
          contract={contract}
          orders={orders?.orders ?? []}
          isLoading={isPending}
          totalCount={orders?.totalCount ?? 0}
          activePage={activePage}
          rowsPerPage={rowsPerPage}
          onPageChange={setActivePage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </div>
  );
};

export default ContractOrders;
